// An audit row as the design shows it: actor, action, details, the coloured dot (DESIGN_SPEC §4.10).
import type { AuditEntry } from '@/api/types';
import type { Tone } from '@/design-system';
import i18n from '@/i18n';
import { formatDateShort, shortName } from '@/lib/format';

export type Outcome = NonNullable<AuditEntry['outcome']>;
export const OUTCOME_TONE: Record<Outcome, Tone> = { success: 'ok', info: 'mute', pending: 'warn', denied: 'err' };

/** The outcome the API will report; until then it is read from the action's name. */
export function outcomeOf(e: AuditEntry): Outcome {
  if (e.outcome) return e.outcome;
  if (/(failed|denied|rejected|reuse|infected)/.test(e.action)) return 'denied';
  if (/(request|pending)/.test(e.action)) return 'pending';
  if (e.actor.kind === 'ai') return 'info';
  return 'success';
}

/** A short summary: the API's text when it has one, else the entity and the most telling field. */
export function summaryOf(e: AuditEntry): string {
  if (e.summary) return e.summary;
  const after = e.after ?? {};
  const pick = ['brand_name', 'name', 'full_name', 'email', 'filename', 'plan', 'purpose', 'section'].map((k) => after[k]).find((v) => typeof v === 'string' && v);
  return [e.entity, typeof pick === 'string' ? pick : e.entity_id.slice(0, 8)].join(' · ');
}

/** «Д. Каримова», «AI · Claude», «Система», «Admin». */
export function actorOf(e: AuditEntry, t: (k: 'ai_actor' | 'system_actor') => string): string {
  if (e.actor.kind === 'ai') return t('ai_actor');
  if (e.actor.kind === 'system') return t('system_actor');
  return shortName(e.actor.label);
}

/** «Сегодня», «Вчера», «3 ноября» (Tashkent days; the year when it is not this year): the list's day dividers. */
export function dayLabel(at: string, nowDate: Date, t: (k: 'today' | 'yesterday') => string): string {
  const key = formatDateShort(at);
  if (key === formatDateShort(nowDate)) return t('today');
  if (key === formatDateShort(new Date(nowDate.getTime() - 86400000))) return t('yesterday');
  const date = new Date(at);
  const sameYear = key.slice(-2) === formatDateShort(nowDate).slice(-2);
  return new Intl.DateTimeFormat(i18n.language === 'uz' ? 'uz-Latn-UZ' : 'ru-RU', {
    timeZone: 'Asia/Tashkent',
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}
